import React from "react";
import { formatMessage } from "lib/globalize-util";
import { $, merge } from "glamor";
import { AlertDialog } from "components/dialog/alert";
import { AlertNotification } from "components/alert/alert-notification";
import { SeverityMessages, ValidationModes } from "constants/product-collection";
import Button from "components/form/button";

interface IProps {
    onRequestClose?: () => void;
    open: boolean;
    title: string;
    desiredFieldNames?: string[];
    bypassDesiredConditions?: boolean;
    itemDisplayProperty?: any;
    desiredFieldsEmpty?: boolean;
    onViewErrorLog?: () => void;
    onCreateCollectionExportItemsPO?: (params: any) => void;
    exportItemsCollection?: any;
    onValidateItemData?: (mode: ValidationModes) => void;
    onBulkItemsClearAll?: () => void;
}

const muiStyles = {
    button: {
        cancel: {
            marginRight: "4px",
        },
        export: {
            marginLeft: "4px",
        },
    },
    buttons: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: "50px",
    },
};
export const ExportWithWarningsDialog: React.FC<IProps> = ({
    title,
    bypassDesiredConditions,
    open,
    onRequestClose,
    desiredFieldNames,
    itemDisplayProperty,
    desiredFieldsEmpty,
    onViewErrorLog,
    onCreateCollectionExportItemsPO,
    onValidateItemData,
    onBulkItemsClearAll,
    exportItemsCollection,
}) => {
    const styles = React.useMemo(
        () =>
            merge(
                $(".notification", {
                    margin: "10px 0",
                    display: "inline-flex",
                })
            ),
        []
    );

    const itemDisplayMap = React.useMemo(() => {
        const itemDisplayPropertyArr = itemDisplayProperty?.toJS ? itemDisplayProperty?.toJS() : itemDisplayProperty;
        return new Map(
            itemDisplayPropertyArr.map((itemDisplayPropertyItem) => [
                itemDisplayPropertyItem.field_name,
                itemDisplayPropertyItem.display_name,
            ])
        );
    }, [itemDisplayProperty]);

    const getFieldNames = React.useCallback(
        (fieldKeyNames: string[]) => fieldKeyNames.map((fieldKeyName) => itemDisplayMap.get(fieldKeyName)),
        [itemDisplayMap]
    );

    const shouldDisableExportButton = React.useMemo(() => {
        if (!bypassDesiredConditions) {
            !desiredFieldsEmpty;
        }
    }, [desiredFieldsEmpty, bypassDesiredConditions]);

    const showDesiredMessage = React.useMemo(() => {
        return desiredFieldNames?.length > 0 && !desiredFieldsEmpty;
    }, [desiredFieldNames, desiredFieldsEmpty]);

    const getWarningMessage = React.useCallback(() => {
        const fieldNames = getFieldNames(desiredFieldNames);

        return formatMessage("Fields with warnings: |desiredFields|", {
            desiredFields: fieldNames.join(", "),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [desiredFieldNames]);

    const handleExportAnyWay = React.useCallback(() => {
        onCreateCollectionExportItemsPO(exportItemsCollection);
        onValidateItemData(undefined);
        onBulkItemsClearAll();
        onRequestClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = React.useCallback(() => {
        onViewErrorLog();
        onBulkItemsClearAll();
        onRequestClose();
    }, [onViewErrorLog, onBulkItemsClearAll, onRequestClose]);

    return (
        <>
            <AlertDialog
                dismissButton={false}
                onRequestClose={() => onRequestClose()}
                open={open}
                title={title}
                contentStyle={{ maxWidth: "600px" }}
            >
                <>
                    <p>
                        {formatMessage(
                            "This assortment still contains some warning messages that your retailer recommends you address before exporting. Close this modal to review your open items or click Export to proceed with your current assortment data."
                        )}
                    </p>
                    {showDesiredMessage && (
                        <AlertNotification
                            className="notification"
                            customStyles={styles}
                            message={getWarningMessage()}
                            severity={SeverityMessages.Warning}
                            open={true}
                            noMargin
                        />
                    )}
                    <footer style={muiStyles.buttons}>
                        <Button label={formatMessage("Close")} onClick={handleClose} style={muiStyles.button.cancel} />
                        <Button
                            label={formatMessage("Export")}
                            primary={true}
                            onClick={handleExportAnyWay}
                            style={muiStyles.button.export}
                            disabled={shouldDisableExportButton}
                        />
                    </footer>
                </>
            </AlertDialog>
        </>
    );
};
